<template>
  <div v-loading="isLoading" class="padded" :role="fetchError ? 'alert' : 'status'">
    <site-details v-if="siteData && siteData.id" :row-data="siteData" :tab="tab" :subtab="subtab" :subsubtab="subsubtab" />
    <template v-else-if="!isLoading">
      <el-alert
        v-if="fetchError"
        type="error"
        :closable="false"
        show-icon
        title="Couldn't load site">
        <span>
          Failed to load the site
          <template v-if="fetchError !== 'network'"> (HTTP {{ fetchError }})</template>
          <template v-else> — network error</template>.
        </span>
        <el-button size="small" style="margin-top: 8px;" @click="fetch">Retry</el-button>
      </el-alert>
      <el-alert
        v-else
        type="info"
        :closable="false"
        show-icon
        title="Site not available">
        <span>This site may not exist, or it belongs to an organisation you don't have access to.</span>
        <el-button size="small" style="margin-top: 8px;" @click="$router.push('/sites')">Back to Sites</el-button>
      </el-alert>
    </template>
  </div>
</template>

<script>
/*
 * L1 layer content for the `d.site.<id>` drawer entry registered in
 * `src/lib/drawerRegistry.js`. Fetches site by id and renders SiteDetails.
 * Uses a raw axios call (not the mixin's fire-and-forget findSite) so the
 * error-state branch and isLoading flag can be wired cleanly.
 */
import axios from '@/lib/axiosClient'

import SiteMixins from '@/mixins/SiteMixins'
import SiteDetails from '@/components/tables/details/SiteDetails'

export default {
  mixins: [SiteMixins],

  components: {
    SiteDetails
  },

  props: {
    siteId: {
      type: Number,
      required: true
    },
    // Forwarded to <site-details> so the active tab is route-driven and
    // deep-linkable (e.g. /sites/site/514/finance).
    tab: {
      type: String,
      default: null
    },
    // Forwarded to <site-details> so each tab's active sub-tab is
    // route-driven and deep-linkable (e.g. /sites/site/514/finance/sharers).
    subtab: {
      type: String,
      default: null
    },
    // Forwarded to <site-details> for 3rd-level sub-tab routing
    // (e.g. /sites/d.site.555.finance.utilities.bill).
    subsubtab: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      siteData: null,
      isLoading: true,
      fetchError: null
    }
  },

  watch: {
    siteId: {
      handler: 'fetch',
      immediate: true
    }
  },

  methods: {
    fetch () {
      this.isLoading = true
      this.fetchError = null
      this.siteData = null

      // Use a raw axios call (not the mixin's fire-and-forget findSite) so we
      // can hook .catch for the error-state branch and .finally for isLoading.
      axios.get(this.getEndpoint('/site/' + this.siteId))
        .then(res => {
          const data = res.data && res.data.data ? res.data.data : res.data
          if (data && data.id) {
            this.siteData = data
          } else {
            this.siteData = null
          }
        })
        .catch(err => {
          this.siteData = null
          this.fetchError = err.response ? err.response.status : 'network'
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>
